// frontend/src/components/vendor/ImageUploader.jsx

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, 
  X, 
  Image as ImageIcon, 
  AlertCircle, 
  CheckCircle,
  Loader,
  Move,
  ZoomIn
} from 'lucide-react';
import { processProductImage, validateImage } from '../../utils/imageProcessor';
import { uploadProductImages } from '../../services/cloudinaryService';
import { useNotification } from '../../context/NotificationContext';

const ImageUploader = ({ images, setImages, maxImages = 10 }) => {
  const [uploadError, setUploadError] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [processingFiles, setProcessingFiles] = useState({});
  const { addNotification } = useNotification();
  
  // Keep track of temporary URLs for cleanup
  const [tempUrls, setTempUrls] = useState([]);

  const processAndUploadImage = async (file) => {
    const fileId = `${file.name}_${Date.now()}`;
    
    try {
      // Start processing
      setProcessingFiles(prev => ({
        ...prev,
        [fileId]: { status: 'processing', progress: 20, name: file.name }
      }));

      // Validate
      validateImage(file);

      // Process image to multiple resolutions
      const processed = await processProductImage(file, file.name);
      
      // Update progress
      setProcessingFiles(prev => ({
        ...prev,
        [fileId]: { ...prev[fileId], status: 'uploading', progress: 50 }
      }));

      // Upload to Cloudinary
      const urls = await uploadProductImages(processed);
      
      // Update progress
      setProcessingFiles(prev => ({
        ...prev,
        [fileId]: { ...prev[fileId], status: 'complete', progress: 100 }
      }));

      // Create preview URL for immediate display
      const previewUrl = URL.createObjectURL(file);
      setTempUrls(prev => [...prev, previewUrl]);

      return {
        id: fileId,
        fileName: file.name,
        fileSize: file.size,
        urls,  // Contains thumbnail, small, medium, large, full, original
        preview: previewUrl,  // For immediate display
        uploadStatus: 'complete',
        metadata: {
          width: processed.original.width,
          height: processed.original.height,
          type: file.type
        }
      };
    } catch (error) {
      console.error('Image processing error:', error);
      
      setProcessingFiles(prev => ({
        ...prev,
        [fileId]: { 
          ...prev[fileId], 
          status: 'error', 
          progress: 0, 
          error: error.message 
        }
      }));
      
      throw error;
    }
  };

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    setUploadError('');

    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(file => {
        if (file.errors[0]?.code === 'file-too-large') {
          return `${file.file.name} is too large (max 10MB)`;
        }
        if (file.errors[0]?.code === 'file-invalid-type') {
          return `${file.file.name} has invalid type`;
        }
        return `${file.file.name} was rejected`;
      });
      setUploadError(errors.join(', '));
      return;
    }

    // Check total images
    if (images.length + acceptedFiles.length > maxImages) {
      setUploadError(`You can only upload up to ${maxImages} images`);
      return;
    }

    
    // SIMPLIFIED: Just store the File objects for display
    try {
      const newImages = acceptedFiles.map(file => ({
        file: file,  // Store the actual File object
        preview: URL.createObjectURL(file),  // For preview display only
        name: file.name,
        id: `${file.name}_${Date.now()}`
      }));
      
      setImages(prev => [...prev, ...newImages]);
      
    } catch (error) {
      setUploadError('Failed to process images. Please try again');
    }
  }, [images, maxImages, setImages]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/heic': ['.heic']
    },
    maxSize: 10485760, // 10MB
    multiple: true,
    disabled: Object.keys(processingFiles).length > 0 // Disable during upload
  });

  const removeImage = (imageId) => {
    const imageToRemove = images.find(img => img.id === imageId);
    if (imageToRemove?.preview && tempUrls.includes(imageToRemove.preview)) {
      URL.revokeObjectURL(imageToRemove.preview);
      setTempUrls(prev => prev.filter(url => url !== imageToRemove.preview));
    }
    setImages(prev => prev.filter(img => img.id !== imageId));
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    
    const newImages = [...images];
    const [movedImage] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    setImages(newImages);
  };

  const isUploading = Object.keys(processingFiles).length > 0;

  // Cleanup temp URLs on unmount
  React.useEffect(() => {
    return () => {
      tempUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [tempUrls]);

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all ${
          isUploading 
            ? 'opacity-50 cursor-not-allowed border-gray-300 bg-gray-50' :
          isDragActive
            ? 'border-blue-500 bg-blue-50 cursor-pointer'
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50 cursor-pointer'
        }`}
      >
        <input {...getInputProps()} disabled={isUploading} />
        
        {isUploading ? (
          <Loader className="mx-auto mb-4 text-blue-500 animate-spin" size={48} />
        ) : (
          <Upload className="mx-auto mb-4 text-gray-400" size={48} />
        )}
        
        <p className="text-lg font-medium text-gray-700 mb-2">
          {isUploading 
            ? 'Processing images...' 
            : isDragActive 
              ? 'Drop images here' 
              : 'Drag & drop images here'}
        </p>
        
        {!isUploading && (
          <>
            <p className="text-sm text-gray-500">or click to select files</p>
            <p className="text-xs text-gray-400 mt-2">
              JPG, PNG, WEBP, HEIC • Max 10MB each • Up to {maxImages} images
            </p>
          </>
        )}
      </div>

      {/* Upload Progress */}
      {Object.keys(processingFiles).length > 0 && (
        <div className="space-y-2">
          {Object.entries(processingFiles).map(([fileId, file]) => (
            <div key={fileId} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium truncate flex-1">
                  {file.name}
                </span>
                <div className="flex items-center space-x-2">
                  {file.status === 'processing' && (
                    <span className="text-xs text-blue-600">Processing...</span>
                  )}
                  {file.status === 'uploading' && (
                    <span className="text-xs text-blue-600">Uploading...</span>
                  )}
                  {file.status === 'complete' && (
                    <CheckCircle className="text-green-500" size={16} />
                  )}
                  {file.status === 'error' && (
                    <AlertCircle className="text-red-500" size={16} />
                  )}
                </div>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    file.status === 'error' ? 'bg-red-500' : 
                    file.status === 'complete' ? 'bg-green-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${file.progress}%` }}
                />
              </div>
              
              {file.error && (
                <p className="text-xs text-red-600 mt-2">{file.error}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Error Message */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
          <AlertCircle size={20} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{uploadError}</p>
        </div>
      )}

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-700">
              Uploaded Images ({images.length}/{maxImages})
            </p>
            <p className="text-xs text-gray-500">
              Drag images to reorder • Click to zoom
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <div
                key={image.id}
                className="relative group aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-300 transition-all"
              >
                {/* Use thumbnail for grid, it's optimized for this size */}
                <img
                  src={image.urls?.thumbnail || image.preview}
                  alt={`Product ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                
                {/* Hover Overlay with Actions */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all">
                  <div className="absolute bottom-0 left-0 right-0 p-2 flex justify-between items-end">
                    {/* Move Controls */}
                    <div className="flex space-x-1">
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => moveImage(index, index - 1)}
                          className="bg-white/90 backdrop-blur text-gray-700 rounded p-1.5 hover:bg-white transition-all"
                          title="Move left"
                        >
                          ←
                        </button>
                      )}
                      {index < images.length - 1 && (
                        <button
                          type="button"
                          onClick={() => moveImage(index, index + 1)}
                          className="bg-white/90 backdrop-blur text-gray-700 rounded p-1.5 hover:bg-white transition-all"
                          title="Move right"
                        >
                          →
                        </button>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex space-x-1">
                      <button
                        type="button"
                        onClick={() => window.open(image.urls?.full || image.preview, '_blank')}
                        className="bg-white/90 backdrop-blur text-gray-700 rounded p-1.5 hover:bg-white transition-all"
                        title="View full size"
                      >
                        <ZoomIn size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(image.id)}
                        className="bg-red-600/90 backdrop-blur text-white rounded p-1.5 hover:bg-red-600 transition-all"
                        title="Remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Badges */}
                {index === 0 && (
                  <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded shadow">
                    Primary
                  </div>
                )}
                
                {/* Size indicator */}
                {image.metadata && (
                  <div className="absolute top-2 right-2 bg-black/60 backdrop-blur text-white text-xs px-2 py-1 rounded">
                    {image.metadata.width}×{image.metadata.height}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Images State */}
      {images.length === 0 && !isUploading && (
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <ImageIcon className="mx-auto mb-2 text-gray-400" size={32} />
          <p className="text-sm text-gray-600">No images uploaded yet</p>
          <p className="text-xs text-gray-500 mt-1">
            High-quality images help customers make purchase decisions
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;